import PropTypes from 'prop-types';
import React from 'react';

import { Tooltip } from '@homeexchange/design';
import { Button } from '@homeexchange/panorama';
import { FinalizeModal, PaymentProvider } from '@homeexchange/payment';

import i18n from '../../i18n';
import { getDynamicUrl } from '../../middleware/lokaliseUrlMiddleware';
import { stringToJSX } from '../../utils/react';

import Conversation from '../../models/Conversation';
import Exchange from '../../models/Exchange';
import Subscription from '../../models/Subscription';
import User from '../../models/User';

import MICExchangeAfterSubEndDatePopup from './MICExchangeAfterSubEndDatePopup';
import Analytics from '../../utils/analytics';
import FinalizeFreeTrialBlocker from './FinalizeFreeTrialBlocker';
import FreeTrialGTM from '../../utils/FreeTrialGTM';
import { connect } from 'react-redux';
import { getConversationsV3 } from '@homeexchange/conversations';
import Utils from '../../utils/utils';

class _ButtonFinalize extends React.Component {
    static propTypes = {
        accessToken: PropTypes.string.isRequired,
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        localeDescription: PropTypes.string,
        subscription: PropTypes.instanceOf(Subscription),
        user: PropTypes.instanceOf(User).isRequired,
        finalizeExchange: PropTypes.func.isRequired
    };

    constructor(props) {
        super(props);
        this.handleFinalization = this.handleFinalization.bind(this);
        this.subscriberFinalization = this.subscriberFinalization.bind(this);
        this.closeExpiredSubscriptionModal = this.closeExpiredSubscriptionModal.bind(this);
        this.closeMICExchangeAfterSubEndDatePopup = this.closeMICExchangeAfterSubEndDatePopup.bind(this);
        this.isExchangeNotCoveredBySub = this.isExchangeNotCoveredBySub.bind(this);
        this.guestNumberIsMissingForApprovedExchange =
            this.guestNumberIsMissingForApprovedExchange.bind(this);
        this.getTooltipContent = this.getTooltipContent.bind(this);
        this.shouldRenderFinalizationButton = this.shouldRenderFinalizationButton.bind(this);
        this.exchangeIsFinalizable = this.exchangeIsFinalizable.bind(this);
        this.closeTrialAlertBlocker = this.closeTrialAlertBlocker.bind(this);
        this.openFinalizeModal = this.openFinalizeModal.bind(this);

        this.state = {
            loading: false,
            showMICExchangeAfterSubEndDatePopup: false,
            showTrialAlertBlocker: false,
            showFinalizationModal: false,
            finalisationIsLoading: false,
            modalHasPurchase: false
        };
    }

    componentDidMount() {
        document.addEventListener('open_finalize_modal', this.openFinalizeModal);
    }

    componentWillUnmount() {
        document.removeEventListener('open_finalize_modal', this.openFinalizeModal);
    }

    openFinalizeModal(event) {
        this.setState({
            showFinalizationModal: true,
            modalHasPurchase: event?.detail?.hasPurchase ?? false
        });
    }

    showFinalizePanel() {
        document.dispatchEvent(
            new CustomEvent('request_card', {
                detail: {
                    view: 'finalization',
                    animate: true
                }
            })
        );
    }

    showPaymentPanel() {
        document.dispatchEvent(
            new CustomEvent('request_card', {
                detail: {
                    view: 'payment',
                    animate: true
                }
            })
        );
    }

    overlapsCurrentExchange(conversations) {
        let conflictedConversation = null;
        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        const currentExchangeRange = moment.range(
            exchangeAsGuest.get('start_on'),
            exchangeAsGuest.get('end_on')
        );
        conversations.some((conversation) => {
            const exchange = conversation.getExchangeAsGuest();
            // Dirty fix, only keep finalized exchanges (todo: change API call to get only finalized exchanges)
            if (exchange && exchange.get('status') === Exchange.STATUS_FINALIZED) {
                // We only consider exchanges as guest
                const exchangeRange = moment.range(exchange.get('start_on'), exchange.get('end_on'));
                if (currentExchangeRange.overlaps(exchangeRange)) {
                    // The exchange the user tries to finalize overlaps a finalized exchange
                    conflictedConversation = conversation;
                    return true;
                }
            }
            return false;
        });

        return conflictedConversation;
    }

    subscriberFinalization() {
        const { conversation, subscription } = this.props;

        const isTrial = subscription.get('is_trial') || false;

        if (isTrial) {
            this.setState({ showTrialAlertBlocker: true });
            FreeTrialGTM.trackFinalizationModalView();
            return;
        }

        this.setState({
            showMICExchangeAfterSubEndDatePopup: false
        });

        if (!conversation.hasToBuyGP()) {
            // Subscribers that don't need to buy GP can finalize directly
            this.setState({
                showFinalizationModal: true,
                modalHasPurchase: false
            });
        } else {
            // Subscribers that need to buy GP are redirected to the payment page
            this.showPaymentPanel();
        }
    }

    isExchangeNotCoveredBySub() {
        const { conversation, subscription } = this.props;
        const guestExchange = conversation.getExchangeAsGuest();
        if (guestExchange.has('will_be_during_subscription_guest')) {
            return (
                !subscription.get('auto_renew') &&
                (guestExchange.get('will_be_during_subscription_guest') === 'not_included' ||
                    guestExchange.get('will_be_during_subscription_guest') === 'partially_included')
            );
        }
        return (
            !subscription.get('auto_renew') &&
            moment(guestExchange.get('end_on')).isAfter(subscription.get('end_on'), 'day')
        );
    }

    goToNextStep() {
        // FIXME: If user is subscriber but GuestPoints's bonus transfert was not done (should not happen),
        // GP balance will be > 0 instead of being 0
        if (this.props.user.isSubscriber()) {
            if (this.isExchangeNotCoveredBySub()) {
                this.setState({
                    showMICExchangeAfterSubEndDatePopup: true
                });
            } else {
                this.subscriberFinalization();
            }
        } else {
            this.showPaymentPanel();
        }
    }

    handleFinalization(event) {
        event.preventDefault();
        if (this.guestNumberIsMissingForApprovedExchange()) {
            return false;
        }

        this.setState({
            loading: true
        });

        getConversationsV3({ filter: 'finalized_only', after: 0, first: 100 }).then((response) => {
            this.setState({
                loading: false
            });
            if (response.data && response.data.conversations) {
                const conversations = response.data.conversations.edges.map(
                    (edge) => new Conversation(edge.node)
                );
                const conflictedConversation = this.overlapsCurrentExchange(conversations);

                if (
                    conflictedConversation &&
                    !this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp('guest')
                ) {
                    const conflictedExchange = conflictedConversation.getExchangeAsGuest();
                    const content = stringToJSX(
                        i18n.t('messaging:dates_conflicts_popup_content', {
                            url: i18n.t('exchange:conversation.url', {
                                id: conflictedConversation.get('id')
                            }),
                            firstname: conflictedExchange.get('host').get('first_name'),
                            location: conflictedExchange.get('home').location(),
                            start: moment(conflictedExchange.get('start_on')).format('L'),
                            end: moment(conflictedExchange.get('end_on')).format('L')
                        })
                    );

                    // The user already has finalized an exchange as guest for these dates, notify him
                    sweetAlert({
                        icon: 'warning',
                        title: i18n.t('messaging:dates_conflicts_popup_title'),
                        content,
                        buttons: [i18n.t('common:no'), i18n.t('common:yes')]
                    }).then((isConfirm) => {
                        if (isConfirm) {
                            this.goToNextStep();
                        }
                    });
                } else {
                    this.goToNextStep();
                }
            } else {
                this.goToNextStep();
            }
        });
    }

    closeExpiredSubscriptionModal() {
        this.setState({
            showExpiredSubscriptionModal: false
        });
    }

    exchangeIsFinalizable() {
        const { conversation } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        return exchangeAsGuest && conversation.isExchangeFinalizable(exchangeAsGuest);
    }

    shouldRenderFinalizationButton() {
        return (
            this.exchangeIsFinalizable() ||
            this.props.conversation.isInExchangeModificationFlowAndShouldBuyGp('guest')
        );
    }

    closeMICExchangeAfterSubEndDatePopup() {
        // send iterable tracking here.
        const { conversation } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        const homePictures = exchangeAsGuest.get('home').get('images');

        try {
            Analytics.trackIterable('AbandonedFinalizationAsGuest', {
                email: this.props.user.get('email'),
                exchangeId: exchangeAsGuest.get('id'),
                exchangeType: exchangeAsGuest.getExchangeType(exchangeAsGuest.get('type')),
                exchangeStartOn: moment(exchangeAsGuest.get('start_on')).format('DD-MM-YYYY'),
                exchangeEndOn: moment(exchangeAsGuest.get('end_on')).format('DD-MM-YYYY'),
                exchangeSumGp: exchangeAsGuest.getGpTotal(),
                nightCount: exchangeAsGuest.get('night_nb'),
                hostId: exchangeAsGuest.get('host').get('id'),
                hostName: exchangeAsGuest.get('host').get('first_name'),
                hostHomeId: exchangeAsGuest.get('home').get('id'),
                hostHomeName: exchangeAsGuest.get('home').get('descriptions')[0].title,
                hostHomeLocation: exchangeAsGuest.get('home').get('address'),
                hostHomePictureUrl: homePictures.length > 0 ? homePictures[0].cdn_link : null,
                conversationId: conversation.get('id')
            });
        } catch (error) {
            console.error('Error while tracking Iterable - AbandonedFinalizationAsGuest', error);
        }
        this.setState({
            showMICExchangeAfterSubEndDatePopup: false
        });
    }

    getTooltipContent() {
        if (this.guestNumberIsMissingForApprovedExchange()) {
            return i18n.t('exchange:finalization.missing_guest_number.tooltip_text');
        }

        const { conversation } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        const exchangeAsHost = conversation.getExchangeAsHost();

        if (exchangeAsHost?.hasStartDatePassed() || exchangeAsGuest?.hasStartDatePassed()) {
            return exchangeAsHost?.hasStartDatePassed() && exchangeAsGuest?.hasStartDatePassed()
                ? i18n.t('exchange:page_messaging_tooltip_both_past_dates_body')
                : i18n.t('exchange:page_messaging_tooltip_one_past_dates_body');
        }
        return null;
    }

    guestNumberIsMissingForApprovedExchange() {
        const exchangeAsGuest = this.props.conversation.getExchangeAsGuest();
        return exchangeAsGuest && exchangeAsGuest.isApproved() && !exchangeAsGuest.get('guest_nb');
    }

    closeTrialAlertBlocker() {
        this.setState({ showTrialAlertBlocker: false });
    }

    finalizeButtonIsDisabled() {
        const { conversation } = this.props;
        const exchangeAsGuest = conversation.getExchangeAsGuest();
        const exchangeAsHost = conversation.getExchangeAsHost();

        return !!(
            this.guestNumberIsMissingForApprovedExchange() ||
            exchangeAsHost?.hasStartDatePassed() ||
            exchangeAsGuest?.hasStartDatePassed()
        );
    }

    render() {
        const { conversation, subscription, finalizeExchange, user } = this.props;
        const { showMICExchangeAfterSubEndDatePopup, showTrialAlertBlocker } = this.state;
        const exchangeAsGuest = conversation.getExchangeAsGuest();

        if (this.shouldRenderFinalizationButton()) {
            const finalizeButtonIsDisabled = this.finalizeButtonIsDisabled();

            return (
                <React.Fragment>
                    <Tooltip placement="top" content={this.getTooltipContent()}>
                        <Button
                            isLoading={this.state.loading}
                            isDisabled={finalizeButtonIsDisabled}
                            onClick={this.handleFinalization}
                            {...(conversation.isInExchangeModificationFlowAndShouldBuyGp('guest') && {
                                id: 'finalize-modification-cta'
                            })}
                            isFullWidth
                        >
                            {conversation.isInExchangeModificationFlowAndShouldBuyGp('guest')
                                ? i18n.t('exchange:Finalise_the_modification_cta')
                                : i18n.t('exchange:action_finalize')}
                        </Button>
                    </Tooltip>
                    <FinalizeFreeTrialBlocker
                        showTrialAlertBlocker={showTrialAlertBlocker}
                        conversationId={conversation.id}
                        closeTrialAlertBlocker={this.closeTrialAlertBlocker}
                    />
                    {subscription && (
                        <MICExchangeAfterSubEndDatePopup
                            show={showMICExchangeAfterSubEndDatePopup}
                            exchange={exchangeAsGuest}
                            subscription={subscription}
                            closeModal={this.closeMICExchangeAfterSubEndDatePopup}
                            onFinalize={this.subscriberFinalization}
                        />
                    )}
                    {this.state.showFinalizationModal && (
                        <PaymentProvider
                            config={{
                                apiUrl: getDynamicUrl(process.env.URL_BFF),
                                assetsUrl: Utils.getAssetsDomain(),
                                locale: this.props.localeDescription
                            }}
                        >
                            <FinalizeModal
                                isOpen={this.state.showFinalizationModal}
                                onClose={() => this.setState({ showFinalizationModal: false })}
                                variant={user.isCollection() ? 'collection' : 'regular'}
                                hasPurchaseGp={this.state.modalHasPurchase}
                                isCtaLoading={this.state.finalisationIsLoading}
                                onCtaClick={() => {
                                    if (this.state.finalisationIsLoading) {
                                        return;
                                    }

                                    this.setState({ finalisationIsLoading: true });

                                    finalizeExchange(conversation.id, user.id).then(
                                        () => {
                                            if (window.hj) {
                                                // Disable issue with URL change
                                                window.hj.locationListener?.setMode('manual');
                                                const locale = i18n.language || 'en';
                                                window.hj('trigger', `exchange-finalization-free-${locale}`);
                                            }
                                            this.setState({
                                                showFinalizationModal: false,
                                                finalisationIsLoading: false
                                            });
                                        },
                                        () => {
                                            this.setState({ finalisationIsLoading: false });
                                        }
                                    );
                                }}
                            />
                        </PaymentProvider>
                    )}
                </React.Fragment>
            );
        }
        return false;
    }
}

const mapStateToProps = (state) => ({
    accessToken: state.auth.accessToken,
    localeDescription: state.localeDescription
});

export default connect(mapStateToProps)(_ButtonFinalize);
